import { Component, computed, DestroyRef, effect, inject, OnDestroy, OnInit, untracked } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LessonDetailStateService } from './lesson-detail-state.service';
import { LessonDetailFormStateService } from './lesson-detail-form-state.service';
import { InstructorLessonDetailComponent } from './instructor-lesson-detail/instructor-lesson-detail.component';
import { LearnerLessonDetailComponent } from './learner-lesson-detail/learner-lesson-detail.component';
import { PageHeaderComponent } from '../page-header/page-header.component';
import { ComponentLoaderService } from '@melodic-ui/services';
import { MDIconComponent } from '@melodic-ui/components';
import { LoaderComponent } from '../loader/loader.component';
import { ResourceErrorComponent, ResourceErrorType } from '../resource-error/resource-error.component';
import { BreadcrumbService } from '../breadcrumb/breadcrumb.service';
import { TimezoneAwareDatePipe } from '@lb/pipes';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { LessonSummaryWebhookService } from './lesson-summary-webhook.service';
import { usesLessonDetailsV2 } from '@lb/functions';

/** Keys for the breadcrumb tail this page inserts under a person shell's override (see ngOnInit). */
const LESSONS_CRUMB_KEY = 'lesson-detail:lessons';
const LESSON_DETAILS_CRUMB_KEY = 'lesson-detail:lesson-details';

/**
 * Lesson-context host for the v2 lesson detail: the page a lesson opens on from anywhere that is not
 * one enrollment — the Lessons pages, Home, the calendars, and the Lessons tab on a person's detail
 * page in admin. Owns its own LessonDetailStateService and its chrome (title, back link, View
 * Enrollment). The enrollment-context host is EnrollmentLessonWrapperComponent, which instead shares
 * the enrollment shell's state, tabs and timeline.
 */
@Component({
	selector: 'lb-lesson-detail',
	templateUrl: './lesson-detail.component.html',
	styleUrl: './lesson-detail.component.scss',
	imports: [RouterModule, MDIconComponent, InstructorLessonDetailComponent, LearnerLessonDetailComponent, LoaderComponent, ResourceErrorComponent],
	standalone: true,
	providers: [LessonDetailStateService, LessonDetailFormStateService, LessonSummaryWebhookService, TimezoneAwareDatePipe]
})
export class LessonDetailComponent implements OnInit, OnDestroy {
	private readonly componentLoaderService = inject(ComponentLoaderService);
	private readonly route = inject(ActivatedRoute);
	private readonly destroyRef = inject(DestroyRef);
	private _router = inject(Router);
	private readonly appSettings = inject<IAppSettings>(APP_CONFIG);
	private readonly breadcrumbService = inject(BreadcrumbService);

	readonly state = inject(LessonDetailStateService);

	/**
	 * URL segments of the lessons list this page belongs to. The detail route is always that list's route
	 * plus the `:lessonId` segment, so dropping the last segment yields the list — `/lessons`,
	 * `/instructor/:id/lessons`, `/learner/:id/lessons`, `/learners/:id/lessons`. Fixed for the life of
	 * the component: only the lesson id can change underneath it.
	 */
	private readonly lessonsListSegments = this.route.snapshot.pathFromRoot.flatMap((r) => r.url.map((s) => s.path)).slice(0, -1);

	/** Back link to the lessons list for the current context; also where ineligible deep links are sent. */
	protected readonly lessonsListLink = this.toUrl(this.lessonsListSegments);

	/**
	 * True on a person page (admin instructor/learner, instructor-app learner), where the shell owns the
	 * breadcrumbs and the page must add its own tail. Off a person page the list has a single segment.
	 */
	private readonly inPersonContext = this.lessonsListSegments.length > 1;

	/**
	 * The lesson's Enrollment Overview in the same context: the list's parent plus `enrollments/:eid` —
	 * `/enrollments/:eid` in the instructor and learner apps, `/{instructor|learner}/:id/enrollments/:eid`
	 * on a person page, so an admin stays inside that person's pages.
	 */
	protected readonly enrollmentLink = computed<string | null>(() => {
		const enrollmentID = this.state.lesson()?.enrollmentID;
		return enrollmentID ? this.toUrl([...this.lessonsListSegments.slice(0, -1), 'enrollments', enrollmentID]) : null;
	});

	lesson = this.state.lesson;
	loading = computed(() => !this.state.detailContentReady() && !this.state.hasError());

	hasError = this.state.hasError;

	// Typed resource-level failure (malformed id / 404 / 403 / other), surfaced inline via <lb-resource-error>.
	protected readonly resourceError = computed<ResourceErrorType | null>(() => {
		const typed = this.state.lessonErrorType();
		if (typed) return typed;
		return this.hasError() ? 'error' : null;
	});

	constructor() {
		// Instantiated eagerly. The form state hydrates itself from LessonDetailStateService through its
		// own effects (see LessonDetailFormStateService's constructor), and creating it here means those
		// effects exist as soon as the lesson and semester arrive rather than when the first detail
		// section mounts. Listing it in `providers` alone would create it lazily, one change-detection
		// cycle after the sections render, and completed lessons would flash an empty form.
		inject(LessonDetailFormStateService);

		// v2 lesson detail only applies to eligible lessons (flag on + enrollment grading config
		// stamped) — the eligibility is only known once the lesson payload has loaded. Ineligible deep
		// links go back to the lessons list this page belongs to (never `/enrollments`, which the
		// instructor app does not have).
		effect(() => {
			const lesson = this.lesson();
			if (!lesson) return;
			if (!usesLessonDetailsV2(lesson, this.appSettings)) {
				untracked(() => this._router.navigateByUrl(this.lessonsListLink, { replaceUrl: true }));
			}
		});
	}

	ngOnInit(): void {
		this.state.navigationSource.set(this.detectNavigationSource());

		const featureEnabled = this.appSettings.features?.enableLessonDetails ?? false;

		if (!featureEnabled) {
			this._router.navigateByUrl(this.lessonsListLink, { replaceUrl: true });
			return;
		}

		// Lesson-scoped title. Deliberately static: the card directly beneath already carries the
		// language, level, date and person, and the enrollment shell's title is the enrollment's.
		this.componentLoaderService.getContainer('banner-main-content').component.set(PageHeaderComponent);
		this.componentLoaderService.getContainer('banner-main-content').inputs.set({ pageTitle: 'Lesson Details' });

		if (this.inPersonContext) {
			// The person shells replace the route crumbs with "Home › Section › {Name} › [Back]", so this
			// route's own breadcrumb data never renders there. Insert "Lessons › Lesson Details" after
			// {Name} and before the shell's Back button. Keyed (recurring) rather than appended so it
			// survives the navigation-end clear of appended crumbs and a lesson-to-lesson param change;
			// removed on destroy. Off a person page the route data supplies the same two crumbs.
			this.breadcrumbService.setRecurringBreadcrumbItem(LESSONS_CRUMB_KEY, { linkText: 'Lessons', routerLink: this.lessonsListLink }, 3);
			this.breadcrumbService.setRecurringBreadcrumbItem(LESSON_DETAILS_CRUMB_KEY, { linkText: 'Lesson Details' }, 4);
		}

		this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
			if (!params.has('lessonId')) return;

			// Call loadLesson even when the id doesn't parse (e.g. /lessons/abc -> NaN) so the
			// state service flags it as an invalid link instead of leaving the view blank.
			this.state.loadLesson(Number(params.get('lessonId')));
		});
	}

	ngOnDestroy(): void {
		this.componentLoaderService.getContainer('banner-main-content').component.set(null);
		this.breadcrumbService.removeRecurringBreadcrumbItem(LESSONS_CRUMB_KEY);
		this.breadcrumbService.removeRecurringBreadcrumbItem(LESSON_DETAILS_CRUMB_KEY);
	}

	private toUrl(segments: (string | number)[]): string {
		return `/${segments.join('/')}`;
	}

	private detectNavigationSource(): { type: 'instructor' | 'learner' | 'enrollment'; id: number } | null {
		let route = this.route.snapshot.parent;

		while (route) {
			const id = route.paramMap.get('id');
			if (id) {
				const parentPath = route.parent?.routeConfig?.path;

				if (parentPath === 'instructor') return { type: 'instructor', id: Number(id) };
				if (parentPath === 'learner') return { type: 'learner', id: Number(id) };
				if (parentPath === 'enrollments') return { type: 'enrollment', id: Number(id) };

				break;
			}
			route = route.parent;
		}

		return null;
	}
}
